import { Binoculars, CaretRight, GearSix, PaperPlaneTilt, SignOut, SunHorizon } from "@phosphor-icons/react";
import { Chip, Count, Logo, Monogram, StatusBadge, cn, type Tone } from "../ui";

/** The real domain behind every company the mocks name, so each row shows the company's own logo. */
const domains: Record<string, string> = {
  Adobe: "adobe.com",
  Anthropic: "anthropic.com",
  Bosch: "bosch.com",
  Databricks: "databricks.com",
  Figma: "figma.com",
  Linear: "linear.app",
  Meta: "meta.com",
  Notion: "notion.so",
  Plaid: "plaid.com",
  Ramp: "ramp.com",
  Salesforce: "salesforce.com",
  Stripe: "stripe.com",
  Vercel: "vercel.com",
};

/** A company's logo tile in a mock. */
export function Co({ name, size = "sm", className }: { name: string; size?: "sm" | "md" | "lg"; className?: string }) {
  return <Monogram name={name} domain={domains[name]} size={size} className={className} />;
}

export function Check({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-4 shrink-0", className)} aria-hidden>
      <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Cross({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-4 shrink-0", className)} aria-hidden>
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function Stars() {
  return (
    <span className="flex gap-0.5 text-accent" role="img" aria-label="5 out of 5 stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden>
          <path d="M10 1.8l2.47 5.2 5.7.7-4.2 3.94 1.08 5.64L10 14.5l-5.05 2.78 1.08-5.64-4.2-3.94 5.7-.7z" />
        </svg>
      ))}
    </span>
  );
}

function SwipeCard({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-card bg-surface p-5 shadow-raised", className)}>
      <div className="flex items-center gap-3">
        <Co name="Linear" size="lg" />
        <div className="min-w-0">
          <p className="truncate text-[15px] font-bold text-ink">Linear</p>
          <p className="whitespace-nowrap text-[13px] text-muted">2 h ago</p>
        </div>
        <Chip tone="brand" className="ml-auto shrink-0 tabular-nums">
          94% fit
        </Chip>
      </div>
      <p className="mt-4 font-display text-[23px] font-bold leading-[1.05] tracking-[-0.035em] text-ink">Senior product designer</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip tone="cream">Remote</Chip>
        <Chip>Full-time</Chip>
        <Chip>$170k–210k</Chip>
      </div>
      <p className="mt-3.5 text-[14px] leading-relaxed text-ink-soft">Own the issue view end to end with two engineers.</p>
    </div>
  );
}

export function PhoneSwipe({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "relative w-[300px] rounded-[50px] bg-[#0b0d12] p-[10px] shadow-[0_40px_90px_-24px_rgba(22,30,48,.4),0_12px_24px_-12px_rgba(22,30,48,.2)] sm:w-[330px]",
        className,
      )}
    >
      <div className="relative overflow-hidden rounded-[40px] bg-page">
        <div className="mx-auto mt-2.5 h-6 w-24 rounded-full bg-[#0b0d12]" />
        <div className="flex items-end justify-between px-5 pb-5 pt-5">
          <div>
            <p className="text-[13px] font-semibold text-primary">Thursday</p>
            <p className="font-display text-[30px] font-extrabold leading-none tracking-[-0.04em] text-ink">Today.</p>
          </div>
          <Chip tone="accent" className="tabular-nums">
            214 new
          </Chip>
        </div>
        <div className="relative px-4">
          <div className="absolute inset-x-8 -bottom-3 h-10 rounded-card bg-primary-tint" />
          <div className="absolute inset-x-6 -bottom-1.5 h-10 rounded-card bg-cream shadow-card" />
          <SwipeCard className="relative" />
        </div>
        <div className="flex items-center justify-center gap-3 px-5 pb-14 pt-7">
          <div className="grid size-14 place-items-center rounded-control bg-surface text-ink shadow-card">
            <Cross className="size-5" />
          </div>
          <div className="flex h-14 flex-1 items-center justify-center gap-2 rounded-control bg-primary text-[16px] font-bold text-on-primary shadow-brand">
            <Check className="size-5" />
            Apply
          </div>
        </div>
      </div>
    </div>
  );
}

export function TossedCard({ company, title, verdict, className }: { company: string; title: string; verdict: "pass" | "apply"; className?: string }) {
  return (
    <div className={cn("relative w-[250px] rounded-card bg-surface p-5 shadow-raised", className)} aria-hidden>
      <div className="flex items-center gap-3">
        <Co name={company} size="md" />
        <p className="text-[15px] font-bold text-ink">{company}</p>
      </div>
      <p className="mt-4 font-display text-[19px] font-bold leading-snug tracking-[-0.03em] text-ink">{title}</p>
      <div className="mt-3 flex gap-1.5">
        <Chip tone="cream">Hybrid</Chip>
        <Chip>Full-time</Chip>
      </div>
      <span
        className={cn(
          "absolute -top-3.5 -skew-x-[14deg] rounded-chip px-3.5 py-1 text-[15px] font-bold tracking-tight shadow-card",
          verdict === "pass" ? "right-5 bg-accent text-on-accent" : "left-5 bg-accent text-on-accent",
        )}
      >
        {verdict === "pass" ? "Pass" : "Apply"}
      </span>
    </div>
  );
}

const appNav = [
  { label: "Today", icon: SunHorizon },
  { label: "Jobs", icon: Binoculars },
  { label: "Applications", icon: PaperPlaneTilt, active: true },
  { label: "Settings", icon: GearSix },
];

const appTabs = [
  { label: "All", count: 21, active: true },
  { label: "Applied", count: 14 },
  { label: "Interview", count: 4 },
  { label: "Offer", count: 1 },
];

const appRows: { company: string; title: string; when: string; status: string; tone: Tone; pulse?: boolean }[] = [
  { company: "Anthropic", title: "Product designer", when: "1 d ago", status: "Applied", tone: "brand" },
  { company: "Meta", title: "Product designer, Threads", when: "Oct 20", status: "Interview", tone: "mint" },
  { company: "Databricks", title: "Senior product designer", when: "3 d ago", status: "Applied", tone: "brand" },
  { company: "Linear", title: "Senior product designer", when: "Oct 18", status: "Offer", tone: "accent" },
  { company: "Stripe", title: "Design engineer", when: "Just now", status: "Sending now", tone: "amber", pulse: true },
];

/** The desktop app, drawn in CSS to match the real Shell: white sidebar with the blue pill, and the applications list. */
function AppWindow({ className }: { className?: string }) {
  return (
    <div className={cn("flex overflow-hidden bg-surface", className)} aria-hidden>
      <div className="hidden w-[196px] shrink-0 flex-col border-r border-line sm:flex">
        <div className="flex h-14 items-center px-4">
          <Logo size={18} />
        </div>
        <div className="flex flex-col gap-0.5 px-2.5 pt-1">
          {appNav.map((n) => (
            <div
              key={n.label}
              className={cn(
                "flex h-9 items-center gap-2.5 rounded-control px-2.5 text-[13px] font-semibold [&_svg]:size-4",
                n.active ? "bg-primary-soft text-primary-ink [&_svg]:text-primary" : "text-ink-soft [&_svg]:text-muted",
              )}
            >
              <n.icon weight="duotone" aria-hidden />
              <span className="flex-1">{n.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto flex items-center gap-2 border-t border-line px-3 py-3">
          <Monogram name="Jordan Lee" logo={false} size="sm" className="size-7 rounded-full text-[11px]" />
          <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-ink-soft">jordan@hey.com</span>
          <SignOut size={14} weight="bold" className="text-muted" aria-hidden />
        </div>
      </div>
      <div className="min-w-0 flex-1 p-5 sm:px-7 sm:py-6">
        <p className="font-display text-[28px] font-extrabold leading-none tracking-[-0.04em] text-ink">Your applications.</p>
        <div className="mt-5 flex gap-1 text-[13px] font-semibold">
          {appTabs.map((t) => (
            <span
              key={t.label}
              className={cn(
                "flex h-8 items-center gap-1.5 rounded-control px-3",
                t.active ? "bg-primary-soft text-primary-ink" : "text-muted",
                t.label === "Offer" && "hidden lg:flex",
              )}
            >
              {t.label} <Count value={t.count} active={t.active} />
            </span>
          ))}
        </div>
        <div className="mt-4 overflow-hidden rounded-card shadow-card">
          {appRows.map((r, i) => (
            <div key={r.company} className={cn("flex items-center gap-3 bg-surface px-3.5 py-3", i > 0 && "border-t border-line")}>
              <Co name={r.company} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-bold text-ink">{r.company}</p>
                <p className="truncate text-[12px] text-muted">
                  {r.title} · {r.when}
                </p>
              </div>
              <StatusBadge tone={r.tone} pulse={r.pulse}>
                {r.status}
              </StatusBadge>
              <CaretRight size={16} weight="bold" className="shrink-0 text-muted" aria-hidden />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** `AppWindow` in a laptop: ink bezel, camera dot, and the hinge and base below. */
export function Laptop({ className }: { className?: string }) {
  return (
    <div className={cn("relative", className)} aria-hidden>
      <div className="rounded-t-[16px] bg-[#0b0d12] p-[10px] pb-3 shadow-[0_40px_80px_-30px_rgba(22,30,48,.55)] ring-1 ring-black/10">
        <span className="absolute left-1/2 top-[4px] size-1 -translate-x-1/2 rounded-full bg-white/25" />
        <AppWindow className="h-[460px] rounded-[4px]" />
      </div>
      <div className="relative -mx-[6%] h-4 rounded-b-[14px] bg-gradient-to-b from-[#d9dce3] to-[#b9bdc8] shadow-[0_18px_30px_-14px_rgba(22,30,48,.5)]">
        <span className="absolute left-1/2 top-0 h-1.5 w-28 -translate-x-1/2 rounded-b-[6px] bg-[#a9aebb]" />
      </div>
    </div>
  );
}

